import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  EMAIL_REGISTERED
} from "../../../utils/Controller/URLConstants";
import {
  launchOpenIDLoginURL,
  launchOpenIDRegistrationURL,
} from "../../../utils/criiptoFunctions";
import { validateEmail } from "../../../utils/FieldValidation";
import useTheme from "../../../hooks/useTheme";
import {
  BackCircleButton,
  CloseCircleButton,
} from "../../UI/CustomButtons";
import { AppColumn, AppRow } from "../../UI/Layouts";
import { SizedBox } from "../../UI/Sizes";
import BankIDVerificationSection from "./Sections/BankIDVerificationSection";
import ContactInformationSection from "./Sections/ContactInformationSection";
import EmploymentTypeSelectionSection from "./Sections/EmploymentTypeSelectionSection";
import LocationSelectionSection from "./Sections/LocationSelectionSection";
import WorkfieldSelectionSection from "./Sections/WorkfieldSelectionSection";
import WorkroleSelectionSection from "./Sections/WorkroleSelectionSection";
import { useResponsive } from "../../UI/ResponsiveContext";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { LANDING_PAGE_PATH, LOGIN_PATH } from "../../../utils/Constants/Paths";

const userInformationPage = "user_information_page";
const selectEmploymentTypes = "select_employment_type";
const selectWorkfieldsPage = "select_workfields";
const selectProfessionPage = "select_profession";
const bankIDverifyPage = "bankId_verify";
const selectLocationPage = "select_location";

const RegistrationStepperComponent = ({ openModal, closeModal, targetPath, initialEmail = "", showToast,asModal }) => {

  const { isMobile } = useResponsive();
  const theme = useTheme();

  const navigate = useNavigate();

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(userInformationPage);
  const [checkingUserExists, setCheckingUserExists] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const openForOpportunitiesPages = [
    userInformationPage,
    selectProfessionPage,
    selectWorkfieldsPage,
    selectLocationPage,
    selectEmploymentTypes,
    bankIDverifyPage,
  ];

  const [formData, setFormData] = useState({
    email: initialEmail,
    professions: [],
    workfields: [],
    locations: [],
    employmentTypes: [],
  });
  const [errors, setErrors] = useState({
    email: "",
    professions: "",
    workfields: "",
    locations: "",
    employmentTypes: "",
    global: null,
  });

  useEffect(() => {
    if (!initialEmail) return;
    setFormData((current) => current.email
      ? current
      : {...current, email: initialEmail});
  }, [initialEmail]);

  const validateAndSetErrors = () => {
    if (currentPage === userInformationPage) {
      if (!validateEmail(formData.email)) {
        setErrors((prevErros) => ({ ...prevErros, email: t('registration.contact_information.error') }));
        return false;
      }
    } else if (currentPage === selectProfessionPage) {
      if (formData.professions.length <= 0) {
        setErrors((prevErros) => ({
          ...prevErros,
          professions: t('registration.select_profession.error'),
        }));
        return false;
      }
    } else if (currentPage === selectWorkfieldsPage) {
      if (formData.workfields.length <= 0) {
        setErrors((prevErros) => ({
          ...prevErros,
          workfields: t('registration.select_workfield.error'),
        }));
        return false;
      }
    } else if (currentPage === selectLocationPage) {
      if (formData.locations.length <= 0) {
        setErrors((prevErros) => ({
          ...prevErros,
          locations: t('registration.select_location.error'),
        }));
        return false;
      }
    } else if (currentPage === selectEmploymentTypes) {
      if (formData.employmentTypes.length <= 0) {
        setErrors((prevErros) => ({
          ...prevErros,
          employmentTypes: t('registration.select_employment_type.error'),
        }));
        return false;
      }
    }
    return true;
  };

  const handleNext = async () => {
    const index = currentPageIndex + 1;

    let valid = validateAndSetErrors();

    let userExists = false;
    if (index === 1) {
      userExists = await checkIfUserExists();
    }

    if (!userExists) {
      if (index > 0 && valid) {
        setCurrentPageIndex(index);
        setCurrentPage(openForOpportunitiesPages[index]);
      }
    }
  };

  const handleBack = () => {
    const index = currentPageIndex - 1;

    if (index >= 0) {
      setCurrentPageIndex(index);
      setCurrentPage(openForOpportunitiesPages[index]);
    } else {
    }
  };

  const updateFormData = (key, value) => {
    setFormData((prevData) => ({ ...prevData, [key]: value }));
  };

  const checkIfUserExists = async () => {
    setCheckingUserExists(true);
    try {
      let response = await axios.get(
        `${EMAIL_REGISTERED}?email=${formData.email}`
      );

      if (response.status === 200 && response.data.status === 0) {

        showToast(t('already_have_account.title'), {
          type: "success",
          duration: 1500,
        });

        setTimeout(() => {
          setCheckingUserExists(false);

          if (isMobile) {
            const loginTarget = targetPath
              ? `${LOGIN_PATH}?target_path=${encodeURIComponent(targetPath)}`
              : LOGIN_PATH;
            navigate(loginTarget);
          } else {
            launchOpenIDLoginURL({ sameDevice: false, targetPath: targetPath });
          }
        }, 1800);

        return true;
      }
    } catch (e) {
      setErrors((prevErros) => ({
        ...prevErros,
        global: "Could not check Authentication",
      }));
      return true;
    }
    setCheckingUserExists(false);
    return false;
  };

  const onCreateAccountClick = async (sameDevice = false) => {
    setLoading(true);
    await launchOpenIDRegistrationURL(formData.email, formData, sameDevice, targetPath);
    setLoading(false);
  };

  return (
    <AppColumn style={{ padding: theme.spacing.contentElementPadding }}>
      <AppRow mainAxisAlignment={"center"} crossAxisAlignment={"center"}>
        {currentPage === userInformationPage ? (
          <CloseCircleButton
            onClick={() => {
              if (asModal) {
                closeModal();
              } else {
                navigate(LANDING_PAGE_PATH);
              }
            }}
          />
        ) : (
          <BackCircleButton onClick={handleBack} />
        )}
        <SizedBox width={theme.spacing.contentElementPadding} />
        {Array.from(
          { length: openForOpportunitiesPages.length },
          (_, index) => {
            const active = index <= currentPageIndex;
            return (
              <div
                key={index}
                style={{
                  width: "100%",
                  height: "0.4rem",
                  marginRight: "1rem",
                  borderRadius: "1rem",
                  backgroundColor: active
                    ? theme.primaryColor
                    : theme.colors.lightGrey,
                }}
              />
            );
          }
        )}
      </AppRow>
      <SizedBox height={theme.spacing.elementPadding} />

      <div>
        {(() => {
          if (currentPage === userInformationPage) {
            return (
              <ContactInformationSection
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
                errors={errors}
                loading={checkingUserExists}
              />
            );
          } else if (currentPage === selectProfessionPage) {
            return (
              <WorkroleSelectionSection
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
                errors={errors}
              />
            );
          } else if (currentPage === selectWorkfieldsPage) {
            return (
              <WorkfieldSelectionSection
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
                errors={errors}
              />
            );
          } else if (currentPage === selectLocationPage) {
            return (
              <LocationSelectionSection
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
                errors={errors}
              />
            );
          } else if (currentPage === selectEmploymentTypes) {
            return (
              <EmploymentTypeSelectionSection
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
                errors={errors}
              />
            );
          } else if (currentPage === bankIDverifyPage) {
            return (

              <BankIDVerificationSection
                onCreateAccount={(sameDevice) => {
                  onCreateAccountClick(sameDevice);
                }}
                isLoading={isLoading}
                onSkip={() => console.log("Skipping BankID verification")}
                onBack={handleBack}
                errors={errors}
              />
            );
          }
        })()}
      </div>
    </AppColumn>
  );
};

export default RegistrationStepperComponent;
